/**
 * restrictedQueryError.ts
 *
 * Typed mapping for the backend's "restricted query" response so any data-load
 * path can surface it as a dedicated empty state instead of swallowing it into a
 * blank result.
 *
 * The backend answers a query it refuses to run (scope too broad for the
 * caller's permissions) with HTTP 420 and `{ "detail": "Query is restricted" }`
 * — e.g. `POST /api/general/well-string?get=true` and `/api/general/section`.
 * That is distinct from a transient/network failure: reloading will not help;
 * the user must narrow their scope. Centralising the predicate keeps the
 * detection out of individual components and lets every restricted load share
 * the same UX, rather than each call site string-matching `detail`.
 */

import type { FetchBaseError } from "@/services/atomApi/types";

/** Non-standard HTTP status the backend returns for a query it refuses to run. */
export const RESTRICTED_QUERY_HTTP_STATUS = 420;

/** Detail string the backend pairs with {@link RESTRICTED_QUERY_HTTP_STATUS}. */
export const RESTRICTED_QUERY_DETAIL = "Query is restricted";

/**
 * True when a failed query response represents a restricted query rather than a
 * transient/unexpected error. Matches on the dedicated 420 status first, then
 * falls back to the detail string so the mapping still holds if the status is
 * normalised elsewhere in the fetch pipeline.
 */
export function isRestrictedQueryError(error: FetchBaseError | null | undefined): boolean {
  if (error === null || error === undefined) {
    return false;
  }
  if (error.status === RESTRICTED_QUERY_HTTP_STATUS) {
    return true;
  }
  return typeof error.detail === "string" && error.detail.includes(RESTRICTED_QUERY_DETAIL);
}
